import { type ReactElement, useMemo, useState } from 'react'
import { isEmpty, isNotEmpty, isTrue, messageFromError } from '@terros/common'
import { Combobox, Input, Pill, PillsInput, useCombobox } from '@mantine/core'
import { useDebounce } from '@hooks'
import {
  MAX_PERMISSION_COMPARE_ENTITIES,
  useTranslation,
  useUniversalSearchQuery,
  type TranslateFunction,
} from '@front/model'
import { DisplaySelectOption, MaterialSymbolIcon, NotReady } from '@components'
import {
  buildCompareEntityOptionGroups,
  type CompareEntity,
  type CompareEntityError,
  type CompareEntityId,
  type CompareEntityKind,
  type CompareRoleOption,
  isCompareEntityId,
  normalizeCompareEntityIds,
} from './compareUtil'

const COMPARE_ENTITY_GROUP_LABELS: Record<CompareEntityKind, string> = {
  user: 'Users',
  team: 'Teams',
  role: 'Roles',
}

type CompareEntityPickerProps = {
  entityIds: CompareEntityId[]
  selectedEntities: CompareEntity[]
  roleOptions: CompareRoleOption[]
  isLoadingRoleOptions: boolean
  isLoadingEntities: boolean
  entitiesError: CompareEntityError
  onChange: (entityIds: CompareEntityId[]) => void
}

export const CompareEntityPicker = ({
  entityIds,
  selectedEntities,
  roleOptions,
  isLoadingRoleOptions,
  isLoadingEntities,
  entitiesError,
  onChange,
}: CompareEntityPickerProps): ReactElement => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput()
      combobox.updateSelectedOptionIndex('active')
      setSearch('')
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption()
    },
  })
  const trimmedSearch = search.trim()
  const debouncedSearch = useDebounce(trimmedSearch, 300)
  const {
    data: searchResults = [],
    isFetching: isFetchingSearch,
    error: searchError,
  } = useUniversalSearchQuery(
    { query: debouncedSearch, scopes: ['User', 'Team'], archived: false },
    isNotEmpty(debouncedSearch)
  )

  const entityOptions = useMemo(
    () =>
      buildCompareEntityOptionGroups({
        searchResults,
        roles: roleOptions,
        search: debouncedSearch,
      }).map((group) => ({
        ...group,
        group: t(COMPARE_ENTITY_GROUP_LABELS[group.group]),
      })),
    [debouncedSearch, roleOptions, searchResults, t]
  )

  const pickerError = searchError ?? entitiesError
  const isWaitingForSearchDebounce = isNotEmpty(trimmedSearch) && trimmedSearch !== debouncedSearch
  const isLoadingSearchResults = isNotEmpty(debouncedSearch) && isFetchingSearch
  const isLoading =
    isWaitingForSearchDebounce ||
    isLoadingSearchResults ||
    isLoadingEntities ||
    (isNotEmpty(trimmedSearch) && isLoadingRoleOptions)
  const isAtEntityLimit = entityIds.length >= MAX_PERMISSION_COMPARE_ENTITIES
  const description = isAtEntityLimit
    ? t('Select up to {{count}} users, teams, or roles. Maximum of {{count}} selected.', {
        count: MAX_PERMISSION_COMPARE_ENTITIES,
      })
    : t('Select up to {{count}} users, teams, or roles. {{selected}}/{{count}} selected.', {
        count: MAX_PERMISSION_COMPARE_ENTITIES,
        selected: entityIds.length,
      })
  const nothingFoundMessage = getNothingFoundMessage({
    isAtEntityLimit,
    isLoading,
    hasSearch: isNotEmpty(trimmedSearch),
    pickerError,
    t,
  })

  const handleOptionSubmit = (value: string): void => {
    if (!isCompareEntityId(value)) return
    if (entityIds.includes(value)) {
      onChange(entityIds.filter((entityId) => entityId !== value))
    } else if (!isAtEntityLimit) {
      onChange(normalizeCompareEntityIds([...entityIds, value]))
    }
    setSearch('')
    combobox.focusSearchInput()
  }

  return (
    <>
      <Combobox offset={0} position='bottom-start' store={combobox} width='target' onOptionSubmit={handleOptionSubmit}>
        <Combobox.Target>
          <PillsInput
            description={description}
            label={t('Add user, team, or role')}
            pointer
            rightSection={isLoading ? <MaterialSymbolIcon icon='progress_activity' /> : <Combobox.Chevron />}
            w='100%'
            onClick={() => combobox.toggleDropdown()}
          >
            <Pill.Group>
              {isEmpty(selectedEntities) ? (
                <Input.Placeholder>{t('Select users, teams, or roles')}</Input.Placeholder>
              ) : (
                selectedEntities.map((entity) => (
                  <Pill
                    key={entity.id}
                    withRemoveButton
                    onRemove={() => onChange(entityIds.filter((entityId) => entityId !== entity.id))}
                  >
                    {entity.name}
                  </Pill>
                ))
              )}
            </Pill.Group>
          </PillsInput>
        </Combobox.Target>
        <Combobox.Dropdown>
          <Combobox.Search
            aria-label={t('Search')}
            placeholder={t('Search')}
            rightSection={isLoading ? <MaterialSymbolIcon icon='progress_activity' /> : undefined}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value)
              combobox.updateSelectedOptionIndex()
            }}
          />
          <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
            {entityOptions.map((group) => (
              <Combobox.Group key={group.group} label={group.group}>
                {group.items.map((option) => {
                  const isSelected = entityIds.includes(option.value)
                  return (
                    <Combobox.Option key={option.value} disabled={isAtEntityLimit && !isSelected} value={option.value}>
                      <DisplaySelectOption isSelected={isSelected} label={option.label} />
                    </Combobox.Option>
                  )
                })}
              </Combobox.Group>
            ))}
            {isEmpty(entityOptions) && <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      {pickerError && <NotReady error={messageFromError(pickerError)} inline />}
    </>
  )
}

const getNothingFoundMessage = ({
  isAtEntityLimit,
  isLoading,
  hasSearch,
  pickerError,
  t,
}: {
  isAtEntityLimit: boolean
  isLoading: boolean
  hasSearch: boolean
  pickerError: CompareEntityError
  t: TranslateFunction
}): string => {
  if (isAtEntityLimit)
    return t('Maximum of {{count}} selected. Remove one to add another.', {
      count: MAX_PERMISSION_COMPARE_ENTITIES,
    })
  if (isLoading) return t('Searching...')
  if (pickerError) return t('Unable to load entities')
  if (!hasSearch) return t('Start typing to search users, teams, and roles')
  return t('No entities found')
}
